import { Ban, Check, Crown, Flag, Gift, MessageCircle, Timer, UserMinus, UserPlus, UserX, X } from '@/components/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { BLAZE_NAME, canActOn, GIFT_MONTHS, giftLength, hasPermission, isTimedOut, Permissions, type UserProfileDTO } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { blazeApi, conversationApi, friendApi, serverApi, userApi } from '@/api/endpoints';
import { MemberRoles } from '@/components/members/MemberRoles';
import { ProfileBio, ProfileCard } from '@/components/profile/ProfileCard';
import { ServerIcon } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Menu } from '@/components/ui/Popover';
import { openCheckout } from '@/lib/paddle';
import { useMemberRank, useMyPermissions } from '@/lib/permissions';
import { formatDay, formatLater } from '@/lib/time';
import { useAuth } from '@/stores/auth';
import { useBlaze } from '@/stores/blaze';
import { useMemberRoles, useServerLook, useServers } from '@/stores/servers';
import { useConversations, useFriends } from '@/stores/social';
import { confirm, toast, useConfirm, useUi } from '@/stores/ui';
import { usePresenceOf, useUser } from '@/stores/users';

export function ProfileModal({ userId, serverId, onClose }: { userId: string; serverId?: string; onClose: () => void }) {
  const cached = useUser(userId);
  const presence = usePresenceOf(userId);
  const myId = useAuth((s) => s.user?.id);
  const [profile, setProfile] = useState<UserProfileDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const openModal = useUi((s) => s.openModal);
  const member = useServers((s) => (serverId ? s.members[serverId]?.[userId] : undefined));
  const serverOwnerId = useServers((s) => (serverId ? s.servers[serverId]?.ownerId : undefined));
  const isFriend = useFriends((s) => !!s.friends[userId]);
  const serverPerms = useMyPermissions(serverId);
  const canNickSelf = serverPerms.can('CHANGE_NICKNAME') || serverOwnerId === myId;

  const load = () =>
    userApi
      .profile(userId)
      .then(setProfile)
      .catch((err) => setError(errorMessage(err)));
  useEffect(() => {
    void load();
  }, [userId, isFriend]);

  // In a server: their profile for it (Blaze) over their own.
  const user = useServerLook(serverId, cached ?? profile?.user ?? null);
  const isSelf = userId === myId;
  const relationship = profile?.relationship ?? (isSelf ? 'self' : 'none');

  const act = async (fn: () => Promise<unknown>, success?: string) => {
    setBusy(true);
    try {
      await fn();
      if (success) toast.success(success);
      await load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const message = async () => {
    try {
      const conversation = await conversationApi.open(userId);
      useConversations.getState().upsert(conversation);
      onClose();
      navigate(`/app/@me/${conversation.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <Modal open onClose={onClose} size="sm" bare>
      <ProfileCard
        user={user}
        presence={presence}
        nickname={member?.nickname}
        badges={serverOwnerId === userId && <Crown size={16} className="shrink-0 text-warning" aria-label="Server owner" />}
        corner={
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-full bg-black/35 p-1.5 text-white hover:bg-black/55">
            <X size={16} />
          </button>
        }
      >
        <div className="mt-4 space-y-4 rounded-xl bg-sunken p-4 text-sm">
          {user && <ProfileBio bio={user.bio} />}
          <section className="flex gap-6">
            <div>
              <h3 className="mb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Member since</h3>
              <p className="text-fg-soft">{user ? formatDay(user.createdAt) : '—'}</p>
            </div>
            {member && (
              <div>
                <h3 className="mb-1 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Joined server</h3>
                <p className="text-fg-soft">{formatDay(member.joinedAt)}</p>
              </div>
            )}
          </section>
          {serverId && member && <MemberRoles serverId={serverId} userId={userId} heading />}
          {profile && profile.mutualServers.length > 0 && (
            <section>
              <h3 className="mb-1.5 text-[11px] font-bold tracking-wider text-fg-muted uppercase">
                Mutual servers — {profile.mutualServers.length}
                {profile.mutualFriendCount > 0 && ` · Mutual friends — ${profile.mutualFriendCount}`}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {profile.mutualServers.slice(0, 8).map((s) => (
                  <span key={s.id} title={s.name}>
                    <ServerIcon server={s} size={28} />
                  </span>
                ))}
              </div>
            </section>
          )}
          {error && <p className="text-danger">{error}</p>}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {isSelf && serverId && member && canNickSelf && (
            <Button
              variant="secondary"
              block
              onClick={() => {
                onClose();
                openModal({ type: 'nickname', serverId, userId });
              }}
            >
              Change nickname
            </Button>
          )}
          {isSelf ? (
            <Button
              variant="secondary"
              block
              onClick={() => {
                onClose();
                openModal({ type: 'userSettings', section: 'profile' });
              }}
            >
              Edit profile
            </Button>
          ) : relationship === 'blocked' ? (
            <>
              <p className="w-full text-sm text-fg-muted">You've blocked this user. You won't get messages, friend requests or notifications from them.</p>
              <Button variant="secondary" block loading={busy} onClick={() => void act(() => userApi.unblock(userId), `Unblocked ${user?.displayName ?? 'user'}.`)}>
                Unblock
              </Button>
            </>
          ) : (
            <>
              <Button icon={<MessageCircle size={16} />} onClick={() => void message()} className="flex-1">
                Message
              </Button>
              {relationship === 'none' && (
                <Button variant="secondary" icon={<UserPlus size={16} />} loading={busy} onClick={() => void act(() => friendApi.request(user!.username), 'Friend request sent.')}>
                  Add friend
                </Button>
              )}
              {relationship === 'outgoing' && (
                <Button variant="secondary" loading={busy} onClick={() => void act(() => friendApi.decline(profile!.friendRequestId!), 'Request cancelled.')}>
                  Cancel request
                </Button>
              )}
              {relationship === 'incoming' && (
                <>
                  <Button variant="success" icon={<Check size={16} />} loading={busy} onClick={() => void act(() => friendApi.accept(profile!.friendRequestId!), 'You are now friends!')}>
                    Accept
                  </Button>
                  <Button variant="secondary" loading={busy} onClick={() => void act(() => friendApi.decline(profile!.friendRequestId!))}>
                    Ignore
                  </Button>
                </>
              )}
              {user && user.staffRole !== 'SYSTEM' && <GiftBlazeButton userId={userId} name={user.displayName} />}
              {relationship === 'friends' && (
                <Button
                  variant="ghost"
                  icon={<UserMinus size={16} />}
                  loading={busy}
                  onClick={async () => {
                    const ok = await confirm({
                      title: `Remove ${user?.displayName}?`,
                      body: 'They will be removed from your friends list.',
                      confirmLabel: 'Remove friend',
                      danger: true,
                    });
                    if (ok) await act(() => friendApi.remove(userId), 'Friend removed.');
                  }}
                >
                  Remove
                </Button>
              )}
              <Button
                variant="ghost"
                icon={<Ban size={16} />}
                loading={busy}
                className="text-danger!"
                onClick={async () => {
                  const ok = await confirm({
                    title: `Block ${user?.displayName}?`,
                    body: "They won't be able to message you or send friend requests, you won't get notifications from them, and their messages in shared servers will be hidden. They aren't told.",
                    confirmLabel: 'Block',
                    danger: true,
                  });
                  if (ok) await act(() => userApi.block(userId), `Blocked ${user?.displayName ?? 'user'}.`);
                }}
              >
                Block
              </Button>
            </>
          )}
          {!isSelf && user && (
            <Button variant="ghost" icon={<Flag size={16} />} className="text-danger!" onClick={() => openModal({ type: 'report', userId, subject: `@${user.username}` })}>
              Report
            </Button>
          )}
        </div>

        {serverId && member && !isSelf && <ModerationActions serverId={serverId} userId={userId} displayName={user?.displayName ?? 'this user'} onDone={onClose} />}
      </ProfileCard>
    </Modal>
  );
}

function ModerationActions({ serverId, userId, displayName, onDone }: { serverId: string; userId: string; displayName: string; onDone: () => void }) {
  const myId = useAuth((s) => s.user?.id ?? '');
  const perms = useMyPermissions(serverId);
  const target = useMemberRank(serverId, userId);

  // Roles are given and taken away in the Roles section above.
  const outranks = canActOn({ userId: myId, rank: perms.rank }, { userId, rank: target.rank, isOwner: target.isOwner });
  const canKick = perms.can('KICK_MEMBERS') && outranks;
  const canBan = perms.can('BAN_MEMBERS') && outranks;
  const canNick = perms.can('MANAGE_NICKNAMES') && outranks;
  // Administrators can't be timed out.
  const targetIsAdmin = useMemberRoles(serverId, userId).some((r) => hasPermission(r.permissions, Permissions.ADMINISTRATOR));
  const canTimeout = perms.can('MODERATE_MEMBERS') && outranks && !targetIsAdmin;
  const timeoutUntil = useServers((s) => s.members[serverId]?.[userId]?.timeoutUntil ?? null);
  const openModal = useUi((s) => s.openModal);
  if (!canKick && !canBan && !canNick && !canTimeout) return null;

  const kick = async () => {
    const result = await useConfirm.getState().open({
      title: `Kick ${displayName}?`,
      body: 'They can rejoin with a new invite.',
      confirmLabel: 'Kick',
      danger: true,
      input: { label: 'Reason (optional)', placeholder: 'Shown in the audit log' },
    });
    if (!result.confirmed) return;
    try {
      await serverApi.kick(serverId, userId, result.value.trim() || undefined);
      useServers.getState().removeMember(serverId, userId);
      toast.success(`${displayName} was kicked.`);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const ban = async () => {
    const result = await useConfirm.getState().open({
      title: `Ban ${displayName}?`,
      body: "They'll be removed and won't be able to rejoin until unbanned.",
      confirmLabel: 'Ban',
      danger: true,
      input: { label: 'Reason (optional)', placeholder: 'e.g. Spamming invites' },
    });
    if (!result.confirmed) return;
    try {
      await serverApi.ban(serverId, userId, result.value.trim() || undefined);
      useServers.getState().removeMember(serverId, userId);
      toast.success(`${displayName} was banned.`);
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <section className="mt-4 border-t border-line pt-4" aria-label="Moderation">
      <h3 className="mb-2 text-[11px] font-bold tracking-wider text-fg-muted uppercase">Moderation</h3>
      {isTimedOut(timeoutUntil) && (
        <p className="mb-3 flex items-center gap-1.5 text-sm text-warning">
          <Timer size={15} /> Timed out until {formatLater(timeoutUntil!)}
        </p>
      )}
      {canNick && (
        <Button
          variant="secondary"
          size="sm"
          className="mb-3"
          onClick={() => {
            onDone();
            openModal({ type: 'nickname', serverId, userId });
          }}
        >
          Change nickname
        </Button>
      )}
      <div className="flex gap-2">
        {canTimeout && (
          <Button
            variant="secondary"
            size="sm"
            icon={<Timer size={15} />}
            onClick={() => {
              onDone();
              openModal({ type: 'timeout', serverId, userId });
            }}
            className="flex-1"
          >
            {isTimedOut(timeoutUntil) ? 'Timeout…' : 'Time out'}
          </Button>
        )}
        {canKick && (
          <Button variant="secondary" size="sm" icon={<UserX size={15} />} onClick={() => void kick()} className="flex-1 text-danger!">
            Kick
          </Button>
        )}
        {canBan && (
          <Button variant="danger" size="sm" icon={<Ban size={15} />} onClick={() => void ban()} className="flex-1">
            Ban
          </Button>
        )}
      </div>
    </section>
  );
}

/** Buys someone Blaze: once it's paid for, the gift reaches them as a DM from you. Shown while gifts are on sale. */
function GiftBlazeButton({ userId, name }: { userId: string; name: string }) {
  const status = useBlaze((s) => s.status);
  const ref = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!useBlaze.getState().status) void useBlaze.getState().refresh();
  }, []);
  const checkout = status?.onSale ? status.checkout : null;
  const kinds = (['gift_month', 'gift_year'] as const).filter((kind) => checkout?.prices[kind]);
  if (!checkout || !kinds.length) return null;

  const buy = async (kind: (typeof kinds)[number]) => {
    setBusy(true);
    try {
      const { transactionId } = await blazeApi.checkout(kind, userId);
      await openCheckout(checkout, transactionId);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Button ref={ref} variant="secondary" icon={<Gift size={16} />} loading={busy} onClick={() => setOpen(true)}>
        Gift Blaze
      </Button>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchor={ref}
        placement="bottom-start"
        label={`Gift ${BLAZE_NAME} to ${name}`}
        items={kinds.map((kind) => ({ label: `${giftLength(GIFT_MONTHS[kind])} of Blaze`, icon: <Gift size={16} />, onSelect: () => void buy(kind) }))}
      />
    </>
  );
}
